// /components/forms/TruckMaintenanceForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    VStack,
    HStack,
    Text,
    Grid,
    Divider,
} from '@chakra-ui/react';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { Save } from 'lucide-react';

interface MaintenanceFormInputs {
    receipt_number: string;
    article: string;
    quantity: number;
    price: number;
    maintenance_date: string;
    fleet_truck_plate_number: string;
}

interface TruckMaintenanceFormProps {
    truckId: number;
    maintenanceId?: number;
    plateNumber: string;
    initialData?: any; // Added for Edit support
    isEdit?: boolean;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export function TruckMaintenanceForm({ 
    truckId, 
    maintenanceId,
    plateNumber, 
    initialData, 
    isEdit = false,
    onSuccess, 
    onCancel 
}: TruckMaintenanceFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showError, showSuccess } = useToastNotification();
    
    // Determine if we are in Edit mode
    //const isEdit = !!initialData;

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<MaintenanceFormInputs>({
        defaultValues: {
            fleet_truck_plate_number: plateNumber,
            maintenance_date: new Date().toISOString().split('T')[0],
            quantity: 1,
            price: 0,
        },
    });

    // --- Effect to load initial data when editing ---
    useEffect(() => {
        if (initialData) {
            reset({
                ...initialData,
                // Ensure the date is formatted correctly for the input type="date"
                maintenance_date: initialData.maintenance_date ? initialData.maintenance_date.split(' ')[0] : '',
            });
        }
    }, [initialData, reset]);

    const watchQty = watch("quantity");
    const watchPrice = watch("price");
    const totalPreview = (Number(watchQty) || 0) * (Number(watchPrice) || 0);

    const onSubmit: SubmitHandler<MaintenanceFormInputs> = async (data) => {
        setIsSubmitting(true);
        try {
            // if (isEdit) {
            //     // UPDATE logic: Path matches your Route::put('{truckId}/maintenance-history/{id}')
            //     await apiClient.put(`/trucks/${truckId}/maintenance-history/${initialData.id}`, data);
            //     showSuccess('Updated', `Maintenance record updated successfully.`);
            // } else {
            //     // CREATE logic: Path matches your Route::post('/truck-maintenance')
            //     await apiClient.post('/trucks/truck-maintenance', data);
            //     showSuccess('Saved', `New maintenance record for ${plateNumber} saved.`);
            // }
            // onSuccess?.();
            if (isEdit) {
                // Use your new PUT API: /trucks/{truckId}/maintenance-history/{id}
                await apiClient.put(`/trucks/truck-maintenance/${maintenanceId}`, data);
            } else {
                await apiClient.post(`/trucks/truck-maintenance`, data);
            }
            onSuccess?.();
        } catch (error: any) {
            showError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="700px">
            <VStack spacing={6} align="stretch">
                
                {/* Plate Number */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>Plate Number</FormLabel>
                    <FormControl>
                        <Input {...register('fleet_truck_plate_number')} isReadOnly variant="filled" bg="gray.100" />
                    </FormControl>
                </Grid>

                {/* Receipt Number */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>
                        Receipt # <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.receipt_number}>
                        <Input {...register('receipt_number', { required: 'Receipt number is required' })} placeholder="REC-001" />
                        <FormErrorMessage>{errors.receipt_number?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Article */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>
                        Article <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.article}>
                        <Input {...register('article', { required: 'Article is required' })} placeholder="e.g. Oil Filter" />
                        <FormErrorMessage>{errors.article?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Quantity */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>
                        Quantity <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.quantity}>
                        <Input 
                            type="number" 
                            {...register('quantity', { required: true, valueAsNumber: true, min: 1 })} 
                        />
                    </FormControl>
                </Grid>

                {/* Unit Price */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>
                        Unit Price <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.price}>
                        <Input 
                            type="number" 
                            step="0.01" 
                            {...register('price', { required: true, valueAsNumber: true, min: 0 })} 
                        />
                    </FormControl>
                </Grid>

                {/* Total Preview Section */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <Box /> 
                    <Box 
                        bg="blue.50" 
                        px={4} 
                        py={3} 
                        borderRadius="md" 
                        borderLeft="4px solid" 
                        borderColor="blue.400"
                    >
                        <Text fontSize="md" fontWeight="bold" color="blue.700">
                            Total: P {totalPreview.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </Text>
                    </Box>
                </Grid>

                {/* Date */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel fontWeight="medium" mb={0}>
                        Date <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.maintenance_date}>
                        <Input type="date" {...register('maintenance_date', { required: 'Date is required' })} />
                    </FormControl>
                </Grid>

                <Divider pt={4} />

                {/* Action Buttons */}
                <HStack spacing={4} justify="flex-end">
                    <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        colorScheme={isEdit ? "green" : "blue"} 
                        isLoading={isSubmitting} 
                        leftIcon={isEdit ? <Save size={18} /> : undefined}
                        px={8}
                    >
                        {isEdit ? 'Update Article' : 'Add Article'}
                    </Button>
                </HStack>
            </VStack>
        </Box>
    );
}